import { computed, reactive, readonly, watch } from 'vue';
import { ayahAudioUrl, findReciter } from '@/utils/quran/audio';
import { useQuranSettings } from '@/composables/quran/useQuranSettings';

/**
 * One recitation player for the whole site — the reader, search results, the
 * ayah of the day and the player bar all drive the same queue, so nothing
 * ever plays over anything else.
 *
 * Plays ayah by ayah from the CDN, buffers the next ayah while the current one
 * plays (no gap between ayat), repeats each ayah as many times as the reader
 * asks (hifz), stops on a sleep timer, and publishes itself to the Media
 * Session API: lock screen, headphones and car controls work like in an app.
 *
 * Queue items: { surah, number, key, arabic, translation: { bs, en }, surahName }
 */
const settings = useQuranSettings();

const state = reactive({
  queue: [],
  index: -1,
  isPlaying: false,
  isLoading: false,
  currentTime: 0,
  duration: 0,
  failed: false,
  // How many times the current ayah has already been recited in a row.
  repeatCount: 0,
  // Sleep timer: null, { until: epoch ms }, or { atEnd: true } (end of the queue).
  sleep: null,
  now: Date.now(),
});

let audio = null;
let prefetch = null;
let sleepTimer = null;
let clock = null;

const current = computed(() => state.queue[state.index] ?? null);
const reciter = computed(() => findReciter(settings.reciterId));

function element() {
  if (audio) return audio;

  audio = new Audio();
  audio.preload = 'auto';
  audio.volume = settings.volume;
  audio.addEventListener('playing', () => {
    state.isPlaying = true;
    state.isLoading = false;
    state.failed = false;
  });
  audio.addEventListener('pause', () => (state.isPlaying = false));
  audio.addEventListener('waiting', () => (state.isLoading = true));
  audio.addEventListener('timeupdate', () => {
    state.currentTime = audio.currentTime;
    updatePositionState();
  });
  audio.addEventListener('loadedmetadata', () => (state.duration = audio.duration || 0));
  audio.addEventListener('ended', onEnded);
  audio.addEventListener('error', () => {
    state.isLoading = false;
    state.isPlaying = false;
    state.failed = true;
  });
  return audio;
}

function onEnded() {
  const times = settings.ayahRepeat;
  if (times === 0 || state.repeatCount + 1 < times) {
    state.repeatCount++;
    return start(state.index, { again: true });
  }
  if (state.index < state.queue.length - 1) return start(state.index + 1);
  if (settings.repeat === 'all' && !state.sleep?.atEnd) return start(0);

  state.isPlaying = false;
  clearSleep();
}

function urlAt(index) {
  const item = state.queue[index];
  return item ? ayahAudioUrl(reciter.value, item.surah, item.number) : null;
}

function start(index, { again = false } = {}) {
  const url = urlAt(index);
  if (!url) return;

  if (!again) state.repeatCount = 0;
  state.index = index;
  state.currentTime = 0;
  state.duration = 0;
  state.isLoading = true;
  state.failed = false;

  const el = element();
  el.src = url;
  el.playbackRate = settings.rate;
  el.play().catch(() => {
    // Autoplay refused or the file failed: leave it paused, ready to retry.
    state.isLoading = false;
    state.isPlaying = false;
  });

  warmNext(index + 1);
  publishMediaSession();
}

function warmNext(index) {
  const url = urlAt(index);
  if (!url) return;
  prefetch ??= new Audio();
  prefetch.preload = 'auto';
  prefetch.src = url;
}

function publishMediaSession() {
  if (!('mediaSession' in navigator) || !current.value) return;

  const item = current.value;
  navigator.mediaSession.metadata = new MediaMetadata({
    title: `${item.surahName} ${item.key}`,
    artist: reciter.value.name,
    album: 'Deenly — Kur’an',
    artwork: [{ src: '/deenly-logo.jpg', sizes: '1024x1024', type: 'image/jpeg' }],
  });

  const handlers = {
    play: () => resume(),
    pause: () => pause(),
    previoustrack: () => previous(),
    nexttrack: () => next(),
    seekto: (details) => details.seekTime != null && seekTo(details.seekTime),
  };
  for (const [action, handler] of Object.entries(handlers)) {
    try {
      navigator.mediaSession.setActionHandler(action, handler);
    } catch {
      // Older browsers don't know every action.
    }
  }
}

function updatePositionState() {
  if (!('mediaSession' in navigator) || !navigator.mediaSession.setPositionState || !state.duration) return;
  try {
    navigator.mediaSession.setPositionState({
      duration: state.duration,
      playbackRate: settings.rate,
      position: Math.min(state.currentTime, state.duration),
    });
  } catch {
    // Position briefly out of range while a new ayah loads.
  }
}

/** Start playing `items` from `startIndex`. */
function play(items, startIndex = 0) {
  state.queue = items;
  start(startIndex);
}

function resume() {
  if (!current.value) return;
  if (state.failed || !audio?.src) return start(state.index);
  audio.play().catch(() => {});
}

function pause() {
  audio?.pause();
}

function toggle() {
  state.isPlaying ? pause() : resume();
}

function next() {
  if (state.index < state.queue.length - 1) start(state.index + 1);
}

function previous() {
  // Like every music player: restart the ayah first, step back on a second press.
  if (audio && audio.currentTime > 3) return seekTo(0);
  if (state.index > 0) start(state.index - 1);
}

/** Jump to any ayah in the queue. */
function jumpTo(index) {
  if (index >= 0 && index < state.queue.length) start(index);
}

function seekTo(seconds) {
  if (audio) audio.currentTime = Math.max(0, Math.min(seconds, state.duration || seconds));
}

function stop() {
  pause();
  clearSleep();
  state.queue = [];
  state.index = -1;
  if ('mediaSession' in navigator) navigator.mediaSession.metadata = null;
}

/** Sleep timer: minutes from now, 'end' (end of the queue) or null to cancel. */
function setSleep(value) {
  clearSleep();
  if (value === 'end') {
    state.sleep = { atEnd: true };
  } else if (typeof value === 'number') {
    state.sleep = { until: Date.now() + value * 60_000 };
    sleepTimer = setTimeout(() => {
      pause();
      clearSleep();
    }, value * 60_000);
    clock = setInterval(() => (state.now = Date.now()), 1000);
  }
}

function clearSleep() {
  clearTimeout(sleepTimer);
  clearInterval(clock);
  state.sleep = null;
}

const isCurrent = (surah, number) => current.value?.surah === surah && current.value?.number === number;

watch(() => settings.rate, (rate) => {
  if (audio) audio.playbackRate = rate;
});

watch(() => settings.volume, (volume) => {
  if (audio) audio.volume = volume;
});

watch(() => settings.reciterId, () => {
  if (current.value && state.isPlaying) start(state.index);
});

export function useQuranPlayer() {
  return {
    state: readonly(state),
    current,
    reciter,
    progress: computed(() => (state.duration ? state.currentTime / state.duration : 0)),
    hasNext: computed(() => state.index < state.queue.length - 1),
    hasPrevious: computed(() => state.index > 0),
    sleepMinutesLeft: computed(() =>
      state.sleep?.until ? Math.max(0, Math.ceil((state.sleep.until - state.now) / 60_000)) : null
    ),
    play,
    resume,
    pause,
    toggle,
    next,
    previous,
    jumpTo,
    seekTo,
    stop,
    setSleep,
    isCurrent,
  };
}

/** Queue items for a surah's ayat, ready for play(). */
export function toQueue(ayahs, surahName) {
  return ayahs.map((a) => ({
    surah: a.surah,
    number: a.number,
    key: a.key,
    arabic: a.arabic,
    translation: a.translation,
    surahName,
  }));
}
